import { useState } from "react"
import axios from "axios"
import { navigate } from "gatsby"
import { useLink } from "../layout/useLink"

// Define the new endpoint for the Zoho integration
const ZOHO_API_ENDPOINT = "/.netlify/functions/zoho-apply-for-jobs"

interface IsState {
  response?: any
  loading: Boolean
  error?: any
}

export const useApplyForJob = () => {
  const [state, setState] = useState<IsState | null>({
    response: null,
    loading: false,
    error: null,
  })

  const thankYouLink = useLink("application-submitted") // The data parameter is now expected to include all text inputs and the file

  const applyForJob = async (data: {
    name: string
    email: string
    phone: string
    portfolioUrl: string
    expectedCtc: string
    currentCtc: string
    totalWorkExperience: string
    jobTitle: string // New field to pass job title
    jobId: string // New field to pass job ID
    file: File
  }) => {
    setState({ response: null, loading: true, error: null })

    const {
      name,
      email,
      phone,
      portfolioUrl,
      expectedCtc,
      currentCtc,
      totalWorkExperience,
      jobTitle,
      jobId,
      file,
    } = data

    try {
      // 1. Prepare the single FormData object for multipart submission
      const formData = new FormData()
      // Append all text fields
      formData.append("name", name)
      formData.append("email", email)
      formData.append("phone", phone)
      formData.append("portfolioUrl", portfolioUrl)
      formData.append("expectedCtc", expectedCtc)
      formData.append("currentCtc", currentCtc)
      formData.append("totalWorkExperience", totalWorkExperience)
      formData.append("jobTitle", jobTitle)
      formData.append("jobId", jobId)

      // Append the file, naming the field 'resume' to match the serverless function
      formData.append("resume", file) // 2. Send the entire multipart form data to the new unified Zoho function

      const zohoSubmission = await axios.post(ZOHO_API_ENDPOINT, formData, {
        headers: {
          // Setting Content-Type is typically not required for FormData with Axios,
          // but we ensure it's multipart. Axios handles the boundary correctly.
          "Content-Type": "multipart/form-data",
        },
      })

      setState({ response: zohoSubmission.data, loading: false, error: null })

      if (thankYouLink) navigate(thankYouLink)
    } catch (error) {
    //   console.log("Zoho job application could not be completed", error) // Use the error message from the response if available, otherwise fallback
      const errorMessage = error.response?.data?.error || error.message
      setState({ response: null, loading: false, error: errorMessage })
    }
  }

  return [applyForJob, state] as const
}
